import React, { useState, useEffect, useCallback } from 'react';
import { useLiveQueue } from '../../hooks/useLiveQueue';
import { useNavigate } from 'react-router-dom';
import { workflowAPI } from '../../lib/api';
import { formatDateTime, formatCurrency } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { 
  DoorOpen, Check, Clock, AlertCircle, CheckCircle, 
  XCircle, CreditCard, Building2, FileText
} from 'lucide-react';
import { toast } from 'sonner';


const REFERENCE_BADGE = {
  prescription: 'bg-red-100 text-red-800',
  lab_test: 'bg-cyan-100 text-cyan-800',
  imaging_test: 'bg-blue-100 text-blue-800',
};

/** Renders loading / empty / children states without nested ternaries. */
const QueueState = ({ loading, empty, emptyContent, children }) => {
  if (loading) return <div className="p-8 text-center text-slate-500">Loading...</div>;
  if (empty) return emptyContent;
  return children;
};

const ReceptionClosure = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientStatus, setPatientStatus] = useState(null);
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [closureData, setClosureData] = useState({
    payment_method: 'cash',
    payment_status: 'pending',
    insurance_company: '',
    insurance_policy: '',
    discharge_summary: '',
    discount_percent: 0,
  });

  const fetchQueue = useCallback(async () => {
    try {
      const response = await workflowAPI.getReceptionQueue();
      setQueue(response.data);
    } catch (error) {
      toast.error('Failed to load reception queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  useLiveQueue('reception', fetchQueue);

  const openClosureDialog = async (patientId) => {
    try {
      const statusRes = await workflowAPI.getPatientStatus(patientId);
      setPatientStatus(statusRes.data);
      setSelectedPatient(queue.find(q => q.patient_id === patientId)?.patient);
      setClosureDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load patient status');
    }
  };

  const handleCloseCase = async () => {
    try {
      // If insurance, set payment as pending
      const finalData = {
        ...closureData,
        payment_status: closureData.payment_method === 'insurance' ? 'pending' : closureData.payment_status
      };
      
      const response = await workflowAPI.closeCase(selectedPatient.id, finalData);
      toast.success(`Case closed! Bill: ${response.data.bill_id} | Amount: ₹${response.data.total_amount}`);
      setClosureDialogOpen(false);
      setClosureData({
        payment_method: 'cash',
        payment_status: 'pending',
        insurance_company: '',
        insurance_policy: '',
        discharge_summary: '',
        discount_percent: 0,
      });
      fetchQueue();
    } catch (error) {
      toast.error('Failed to close case');
    }
  };

  // Group queue items by patient
  const patientGroups = queue.reduce((acc, item) => {
    const pid = item.patient_id;
    if (!acc[pid]) {
      acc[pid] = {
        patient: item.patient,
        items: []
      };
    }
    acc[pid].items.push(item);
    return acc;
  }, {});

  return (
    <div data-testid="reception-closure-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Case Closure - Reception</h1>
            <p className="text-slate-500">Patients ready for discharge & billing</p>
          </div>
          <Badge className="bg-blue-500 text-white text-lg px-4 py-2">
            {Object.keys(patientGroups).length} Patients
          </Badge>
        </div>
      </header>

      <div className="page-content space-y-4">
        <QueueState loading={loading} empty={Object.keys(patientGroups).length === 0} emptyContent={(
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              <DoorOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No patients ready for discharge</p>
            </CardContent>
          </Card>
        )}>
          {(
          Object.entries(patientGroups).map(([patientId, group]) => (
            <Card key={patientId}>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold font-[Manrope]">
                      {group.patient?.full_name || 'Unknown Patient'}
                    </CardTitle>
                    <p className="text-sm text-slate-500">ID: {group.patient?.patient_id}</p>
                  </div>
                  <Button
                    className="bg-red-500 hover:bg-red-600"
                    onClick={() => openClosureDialog(patientId)}
                  >
                    <DoorOpen className="w-4 h-4 mr-2" />
                    Close Case & Discharge
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-2">
                  {group.items.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge className={REFERENCE_BADGE[item.reference_type] || 'bg-violet-100 text-violet-800'}>
                          {item.reference_type?.replace('_', ' ')}
                        </Badge>
                        <div>
                          <p className="text-sm font-medium">{item.reason}</p>
                          <p className="text-xs text-slate-500">From: {item.referred_by_name}</p>
                        </div>
                      </div>
                      <Badge className="bg-amber-100 text-amber-800">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatDateTime(item.created_at)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
          )}
        </QueueState>
      </div>

      {/* Case Closure Dialog */}
      <Dialog open={closureDialogOpen} onOpenChange={setClosureDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              Close Case - {selectedPatient?.full_name}
            </DialogTitle>
          </DialogHeader>
          
          {patientStatus && (
            <div className="space-y-6 mt-4">
              {/* Checklist */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold">Completion Checklist</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Prescriptions</span>
                    <div className="flex items-center gap-2">
                      {patientStatus.checklist.prescriptions.completed > 0 ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-slate-300" />
                      )}
                      <span className="text-sm text-slate-600">
                        {patientStatus.checklist.prescriptions.completed} dispensed
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Lab Tests</span>
                    <div className="flex items-center gap-2">
                      {patientStatus.checklist.lab_tests.completed > 0 ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-slate-300" />
                      )}
                      <span className="text-sm text-slate-600">
                        {patientStatus.checklist.lab_tests.completed} completed
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Imaging</span>
                    <div className="flex items-center gap-2">
                      {patientStatus.checklist.imaging.completed > 0 ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-slate-300" />
                      )}
                      <span className="text-sm text-slate-600">
                        {patientStatus.checklist.imaging.completed} completed
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Procedures</span>
                    <div className="flex items-center gap-2">
                      {patientStatus.checklist.procedures.completed > 0 ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-slate-300" />
                      )}
                      <span className="text-sm text-slate-600">
                        {patientStatus.checklist.procedures.completed} completed
                      </span>
                    </div>
                  </div>
                  
                  {(patientStatus.checklist.prescriptions.pending > 0 || 
                    patientStatus.checklist.lab_tests.pending > 0 ||
                    patientStatus.checklist.imaging.pending > 0 ||
                    patientStatus.checklist.procedures.pending > 0) && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <p className="text-sm text-amber-800">
                          <strong>Warning:</strong> Patient has pending items. Ensure all are complete before discharge.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Billing & Payment */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-red-500" />
                    Billing & Payment
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Payment Method *</Label>
                      <Select value={closureData.payment_method} onValueChange={(v) => setClosureData({...closureData, payment_method: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent position="popper" sideOffset={5}>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="card">Card</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="insurance">Insurance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {closureData.payment_method !== 'insurance' && (
                      <div>
                        <Label>Payment Status *</Label>
                        <Select value={closureData.payment_status} onValueChange={(v) => setClosureData({...closureData, payment_status: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {closureData.payment_method === 'insurance' && (
                    <>
                      <div>
                        <Label>Insurance Company *</Label>
                        <Input
                          value={closureData.insurance_company}
                          onChange={(e) => setClosureData({...closureData, insurance_company: e.target.value})}
                          placeholder="e.g., Star Health, ICICI Lombard"
                          required
                        />
                      </div>
                      <div>
                        <Label>Policy Number *</Label>
                        <Input
                          value={closureData.insurance_policy}
                          onChange={(e) => setClosureData({...closureData, insurance_policy: e.target.value})}
                          placeholder="Enter policy number"
                          required
                        />
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800">
                          <Building2 className="w-4 h-4 inline mr-1" />
                          Bill will be submitted to insurance company for reimbursement
                        </p>
                      </div>
                    </>
                  )}

                  <div>
                    <Label>Discount % (Optional)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={closureData.discount_percent}
                      onChange={(e) => setClosureData({...closureData, discount_percent: parseFloat(e.target.value) || 0})}
                      placeholder="0"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Discharge Summary */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-red-500" />
                    Discharge Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={closureData.discharge_summary}
                    onChange={(e) => setClosureData({...closureData, discharge_summary: e.target.value})}
                    placeholder="Enter discharge summary, instructions, follow-up notes..."
                    rows={5}
                  />
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setClosureDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  className="bg-red-500 hover:bg-red-600"
                  onClick={handleCloseCase}
                  disabled={closureData.payment_method === 'insurance' && (!closureData.insurance_company || !closureData.insurance_policy)}
                >
                  <DoorOpen className="w-4 h-4 mr-2" />
                  Close Case & Generate Bill
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="page-content">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <DoorOpen className="w-5 h-5 text-red-500" />
              Patients Ready for Discharge
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <QueueState loading={loading} empty={Object.keys(patientGroups).length === 0} emptyContent={(
              <div className="p-8 text-center text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No patients waiting for discharge</p>
              </div>
            )}>
              {(
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Pending Items</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(patientGroups).map(([patientId, group]) => (
                    <TableRow key={patientId}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{group.patient?.full_name}</p>
                          <p className="text-xs text-slate-500">{group.patient?.patient_id}</p>
                          {group.patient?.phone && (
                            <p className="text-xs text-slate-500">📞 {group.patient.phone}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {group.items.map((item) => (
                            <Badge key={item.id} variant="outline" className="text-xs">
                              {item.reference_type?.replace('_', ' ')}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(group.items[0]?.created_at)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          className="bg-blue-500 hover:bg-blue-600"
                          onClick={() => openClosureDialog(patientId)}
                        >
                          Process Discharge
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </QueueState>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReceptionClosure;
